import type { BusinessHours, ResponderRule, RuleConditions, RuleEvaluation } from './types.js';
import { addressMatches } from './safety.js';

export interface RuleMessage {
  mailboxId: string;
  fromEmail: string;
  subject: string;
  bodyText: string;
  receivedAt: Date;
  category: string | null;
  language: string | null;
}

export function emptyConditions(): RuleConditions {
  return {
    senders: [],
    domains: [],
    subjectPhrases: [],
    bodyPhrases: [],
    categories: [],
    languages: [],
    businessHours: { enabled: false, days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00', timezone: 'UTC', outsideOnly: false },
    excludeSenders: [],
    excludeDomains: [],
    excludeSubjectPhrases: [],
  };
}

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Local weekday and minutes-of-day for a date in an IANA time zone. */
export function zonedParts(date: Date, timeZone: string): { weekday: number; minutes: number } {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday ?? 'Sun');
  return { weekday, minutes: parseInt(parts.hour ?? '0', 10) * 60 + parseInt(parts.minute ?? '0', 10) };
}

/** True if `minutes` lies in [start, end), handling windows that cross midnight. */
export function inWindow(minutes: number, start: string, end: string): boolean {
  const s = minutesOf(start);
  const e = minutesOf(end);
  if (s === e) return true;
  return s < e ? minutes >= s && minutes < e : minutes >= s || minutes < e;
}

export function withinBusinessHours(bh: BusinessHours, at: Date): boolean {
  const { weekday, minutes } = zonedParts(at, bh.timezone || 'UTC');
  return bh.days.includes(weekday) && inWindow(minutes, bh.start, bh.end);
}

const has = (hay: string, needles: string[]) => {
  const h = hay.toLowerCase();
  return needles.find((n) => n.trim() && h.includes(n.trim().toLowerCase()));
};

/** Evaluate a single rule and explain the outcome. All listed positive conditions must hold (AND); empty lists are ignored. */
export function evaluateRule(rule: ResponderRule, msg: RuleMessage): RuleEvaluation {
  const reasons: string[] = [];
  const c = rule.conditions;
  const fail = (r: string): RuleEvaluation => ({ ruleId: rule.id, ruleName: rule.name, matched: false, reasons: [...reasons, r] });

  if (!rule.enabled) return fail('Rule is disabled');
  if (rule.mailboxId && rule.mailboxId !== msg.mailboxId) return fail('Rule applies to a different mailbox');

  if (c.excludeSenders.length && addressMatches(msg.fromEmail, c.excludeSenders)) return fail(`Sender ${msg.fromEmail} is excluded`);
  if (c.excludeDomains.length && addressMatches(msg.fromEmail, c.excludeDomains)) return fail(`Sender domain is excluded`);
  const exSubj = has(msg.subject, c.excludeSubjectPhrases);
  if (exSubj) return fail(`Subject contains excluded phrase "${exSubj}"`);

  const senderList = [...c.senders, ...c.domains];
  if (senderList.length) {
    if (!addressMatches(msg.fromEmail, senderList)) return fail(`Sender ${msg.fromEmail} is not in the rule's senders/domains`);
    reasons.push(`Sender ${msg.fromEmail} matches senders/domains`);
  }
  if (c.subjectPhrases.length) {
    const p = has(msg.subject, c.subjectPhrases);
    if (!p) return fail('Subject does not contain any required phrase');
    reasons.push(`Subject contains "${p}"`);
  }
  if (c.bodyPhrases.length) {
    const p = has(msg.bodyText, c.bodyPhrases);
    if (!p) return fail('Body does not contain any required phrase');
    reasons.push(`Body contains "${p}"`);
  }
  if (c.categories.length) {
    if (!msg.category) return fail('Message category is not known yet (AI classification unavailable)');
    if (!c.categories.map((x) => x.toLowerCase()).includes(msg.category.toLowerCase()))
      return fail(`Category "${msg.category}" is not one of ${c.categories.join(', ')}`);
    reasons.push(`Category is "${msg.category}"`);
  }
  if (c.languages.length) {
    if (!msg.language) return fail('Message language is not known');
    if (!c.languages.map((x) => x.toLowerCase()).includes(msg.language.toLowerCase()))
      return fail(`Language "${msg.language}" is not one of ${c.languages.join(', ')}`);
    reasons.push(`Language is "${msg.language}"`);
  }
  if (c.businessHours.enabled) {
    const inside = withinBusinessHours(c.businessHours, msg.receivedAt);
    if (c.businessHours.outsideOnly && inside) return fail('Received during business hours; rule only runs outside them');
    if (!c.businessHours.outsideOnly && !inside) return fail('Received outside business hours');
    reasons.push(inside ? 'Received during business hours' : 'Received outside business hours');
  }
  if (!reasons.length) reasons.push('Rule has no narrowing conditions, so it matches every eligible message');
  return { ruleId: rule.id, ruleName: rule.name, matched: true, reasons };
}

/** Evaluate rules in priority order; the first match wins. */
export function evaluateRules(rules: ResponderRule[], msg: RuleMessage): { evaluations: RuleEvaluation[]; matched: ResponderRule | null } {
  const sorted = [...rules].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt));
  const evaluations: RuleEvaluation[] = [];
  let matched: ResponderRule | null = null;
  for (const r of sorted) {
    const ev = evaluateRule(r, msg);
    if (matched) {
      evaluations.push({ ...ev, matched: false, reasons: [`Not evaluated further: "${matched.name}" matched first (higher priority)`] });
      continue;
    }
    evaluations.push(ev);
    if (ev.matched) matched = r;
  }
  return { evaluations, matched };
}

/** Fill an acknowledgement template. Only safe, factual placeholders are supported — no invented promises. */
export function renderAckTemplate(tpl: string, vars: { senderName: string | null; subject: string; myName: string }): string {
  return tpl
    .replace(/\{\{\s*sender_name\s*\}\}/gi, vars.senderName?.split(' ')[0] || 'there')
    .replace(/\{\{\s*subject\s*\}\}/gi, vars.subject)
    .replace(/\{\{\s*my_name\s*\}\}/gi, vars.myName);
}

export const DEFAULT_ACK_TEMPLATE =
  'Hi {{sender_name}},\n\nThank you for your email. We have received it and will get back to you shortly.\n\nBest regards,\n{{my_name}}';
